/**
 * FieldCard Component
 * Groups related fields into a compact card with optional 2-column grid layout
 */

import React from 'react';
import {
    Stack,
    Text,
    mergeStyles,
} from '@fluentui/react';

// Card container styles
const cardStyles = mergeStyles({
    backgroundColor: '#fafbfc',
    borderRadius: '8px',
    border: '1px solid #e8eaed',
    padding: '16px',
    marginBottom: '12px',
});

const cardTitleStyles = mergeStyles({
    fontSize: '13px',
    fontWeight: 600,
    color: '#444',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
});

const gridStyles = mergeStyles({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    '@media (max-width: 768px)': {
        gridTemplateColumns: '1fr',
    },
});

const singleColumnStyles = mergeStyles({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
});

interface FieldCardProps {
    title?: string;
    icon?: React.ReactNode;
    children: React.ReactNode;
    columns?: 1 | 2;
    className?: string;
}

export const FieldCard: React.FC<FieldCardProps> = ({
    title,
    icon,
    children,
    columns = 2,
    className,
}) => {
    return (
        <div className={`${cardStyles} ${className || ''}`}>
            {title && (
                <div className={cardTitleStyles}>
                    {icon}
                    {title}
                </div>
            )}
            <div className={columns === 2 ? gridStyles : singleColumnStyles}>
                {children}
            </div>
        </div>
    );
};

// Inline Field component for use within FieldCard (compact 2-col layout)
interface InlineFieldProps {
    label: string;
    description?: string;
    children: React.ReactNode;
    fullWidth?: boolean;
    highlighted?: boolean;
}

const inlineFieldStyles = mergeStyles({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
});

const fullWidthFieldStyles = mergeStyles({
    gridColumn: '1 / -1',
});

const highlightedStyles = mergeStyles({
    backgroundColor: '#fff8e6',
    padding: '12px',
    borderRadius: '6px',
    border: '1px solid #ffd966',
    margin: '-4px',
});

const inlineLabelStyles = mergeStyles({
    fontSize: '13px',
    fontWeight: 500,
    color: '#333',
});

const inlineDescStyles = mergeStyles({
    fontSize: '11px',
    color: '#666',
    lineHeight: '1.3',
});

export const InlineField: React.FC<InlineFieldProps> = ({
    label,
    description,
    children,
    fullWidth = false,
    highlighted = false,
}) => {
    return (
        <div className={`${inlineFieldStyles} ${fullWidth ? fullWidthFieldStyles : ''} ${highlighted ? highlightedStyles : ''}`}>
            <Text className={inlineLabelStyles}>{label}</Text>
            {children}
            {description && (
                <Text className={inlineDescStyles}>{description}</Text>
            )}
        </div>
    );
};

// Credential Field component for sensitive fields
interface CredentialFieldProps {
    label: string;
    value: string;
    isKeyVault?: boolean;
    hasValue?: boolean;       // Whether the key actually has a value (from backend check)
    valueSource?: string | null;  // 'env', 'cosmos', or null
    highlighted?: boolean;
}

const credentialRowStyles = mergeStyles({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    border: '1px solid #e1e5e9',
    gap: '12px',
});

const credentialLabelStyles = mergeStyles({
    fontSize: '13px',
    fontWeight: 500,
    color: '#333',
    minWidth: '120px',
});

const credentialValueStyles = mergeStyles({
    fontSize: '13px',
    color: '#666',
    fontFamily: 'monospace',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const keyVaultBadgeStyles = mergeStyles({
    backgroundColor: '#ede9fe',
    color: '#667eea',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
});

const envBadgeStyles = mergeStyles({
    backgroundColor: '#dcfce7',
    color: '#166534',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
});

const miBadgeStyles = mergeStyles({
    backgroundColor: '#dbeafe',
    color: '#0078d4',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
});

export const CredentialField: React.FC<CredentialFieldProps> = ({
    label,
    value,
    isKeyVault = false,
    hasValue: hasValueProp,
    valueSource,
    highlighted = false,
}) => {
    // Use the prop if provided, otherwise fall back to checking the value string
    const hasValue = hasValueProp !== undefined 
        ? hasValueProp 
        : (value && value !== 'Not configured' && value !== '' && !value.startsWith('@Microsoft.KeyVault('));
    
    // Determine display status based on whether this is a Key Vault managed field
    let displayValue: string;
    let statusColor: string;
    let showBadge = false;
    let badgeText = '';
    let badgeStyle = keyVaultBadgeStyles;
    
    if (isKeyVault) {
        if (hasValue) {
            // Key has a value - show appropriate source
            if (valueSource === 'env') {
                displayValue = '✓ Set via ENV / Key Vault';
                statusColor = '#166534';  // Dark green
                showBadge = true;
                badgeText = '🔐 ENV';
                badgeStyle = envBadgeStyles;
            } else {
                displayValue = '✓ Stored in Key Vault';
                statusColor = '#107c10';  // Green
                showBadge = true;
                badgeText = '🔑 Key Vault';
            }
        } else {
            // Key Vault field but no key set - using Managed Identity
            displayValue = '🔐 Using MI (key not set)';
            statusColor = '#0078d4';  // Blue
            showBadge = true;
            badgeText = 'MI Auth';
            badgeStyle = miBadgeStyles;
        }
    } else {
        displayValue = value || 'Not configured';
        statusColor = value ? '#666' : '#999';
    }
    
    return (
        <div className={`${credentialRowStyles} ${highlighted ? highlightedStyles : ''}`}>
            <Text className={credentialLabelStyles}>{label}</Text>
            <Text className={credentialValueStyles} style={{ color: statusColor, fontFamily: 'inherit' }}>
                {displayValue}
            </Text>
            {showBadge && (
                <span className={badgeStyle}>{badgeText}</span>
            )}
        </div>
    );
};

// Action Button Group component
interface ActionButtonGroupProps {
    children: React.ReactNode;
}

const actionGroupStyles = mergeStyles({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '12px 0',
});

export const ActionButtonGroup: React.FC<ActionButtonGroupProps> = ({ children }) => {
    return (
        <div className={actionGroupStyles}>
            {children}
        </div>
    );
};

export default FieldCard;
